import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import { SyntheticEvent, useEffect, useState } from "react";
import { StyledSuggestedSearch, SuggestedSearchPopper, SuggestedSearchTextField } from "./SuggestedSearch.styles";
import { Button, IconButton, InputAdornment, PopperProps } from "@mui/material";
import { SearchButtonText } from "constants/text.ts";

interface SuggestedSearchProps {
	autoFocus?: boolean;
	ariaLabel: string;
	dataTestId: string;
	dropDownOpenOnChar: number;
	placeholder: string;
	id: string;
	// options to render within the presentation portal.
	searchOptions: Array<string>;
	searchValue: string;
	handleSearch: (e: SyntheticEvent, v: string | null) => void;
	size: "small" | "medium" | undefined;
	error?: boolean;
	hasButton?: boolean;
}

const CustomPopper = (props: PopperProps) => {
	return <SuggestedSearchPopper {...props} />;
};

const SuggestedSearch = ({
	autoFocus,
	ariaLabel,
	dataTestId,
	dropDownOpenOnChar,
	placeholder,
	handleSearch,
	id,
	searchOptions,
	searchValue,
	size,
	error,
	hasButton
}: SuggestedSearchProps) => {
	const [open, setOpen] = useState(false);
	const [searchInput, setSearchInput] = useState("");
	const [arrowKeyUsed, setArrowKeyUsed] = useState(false);
	const [isFocused, setIsFocused] = useState(false);

	const isOptionEqualToValue = (option: string, value: string) => {
		return option.toUpperCase().includes(value.toUpperCase());
	};

	// Sync internal searchInput state with searchValue prop
	useEffect(() => {
		setSearchInput(searchValue || "");
	}, [searchValue]);

	return (
		<>
			<StyledSuggestedSearch
				id={id}
				open={open}
				inputValue={searchInput}
				onInputChange={(event, value, reason) => {
					if (reason === "clear") {
						setSearchInput("");
						handleSearch(event, "");
						setOpen(false);
						return;
					}
					if (reason === "reset") {
						return;
					}
					setSearchInput(value);
					const shouldOpen = value.length >= dropDownOpenOnChar;
					setOpen(shouldOpen);
					setArrowKeyUsed(false);
				}}
				onClose={(_, reason) => {
					if (reason === "blur" || reason === "escape") {
						setOpen(false);
					}
				}}
				onChange={(event, value) => {
					if (value === null) {
						setSearchInput("");
						handleSearch(event, "");
						setOpen(false);
						return;
					}
					if (value) {
						setSearchInput(value);
						handleSearch(event, value);
						setOpen(false);
						setArrowKeyUsed(false);
					}
				}}
				autoSelect={false}
				selectOnFocus={false}
				disableClearable={false}
				freeSolo
				onKeyDown={(event) => {
					if (event.key === "Enter") {
						if (!arrowKeyUsed) {
							event.defaultMuiPrevented = true;
							handleSearch(event, searchInput.trim());
						}
						setOpen(false);
					} else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
						setArrowKeyUsed(true);
					}
				}}
				value={searchInput || ""}
				isOptionEqualToValue={isOptionEqualToValue}
				aria-label={ariaLabel}
				data-testid={dataTestId}
				options={Array.from(new Set(searchOptions))}
				filterOptions={(options, state) => {
					const query = state.inputValue.trim().toLowerCase() || searchInput;
					return options.filter((option) => option.toLowerCase().includes(query));
				}}
				size={size}
				clearIcon={
					<CloseIcon
						onMouseDown={(e) => {
							e.preventDefault();
							setSearchInput("");
							handleSearch(e, "");
							setOpen(false);
						}}
					/>
				}
				renderInput={(params) => (
					<SuggestedSearchTextField
						{...params}
						autoFocus={autoFocus}
						error={error}
						data-testid="lineItem-search-input"
						placeholder={placeholder}
						variant="outlined"
						onFocus={() => setIsFocused(true)}
						onBlur={() => setIsFocused(false)}
						InputProps={{
							...params.InputProps,
							endAdornment:
								searchInput && isFocused ? (
									<>
										{params.InputProps.endAdornment}
										<InputAdornment position="end">
											<IconButton
												edge="end"
												onMouseDown={(e) => {
													e.preventDefault();
													handleSearch(e, searchInput.trim());
													setOpen(false);
												}}
												sx={{ padding: "8px" }}
											>
												<SearchIcon />
											</IconButton>
										</InputAdornment>
									</>
								) : null
						}}
					/>
				)}
				PopperComponent={CustomPopper}
			/>
			{hasButton && (
				<Button
					variant="contained"
					color="primary"
					onMouseDown={(e) => {
						e.preventDefault();
						handleSearch(e, searchInput.trim());
						setOpen(false);
					}}
				>
					{SearchButtonText}
				</Button>
			)}
		</>
	);
};

export default SuggestedSearch;
